import React from "react";
import PropTypes from "prop-types";
import CarouselById from "../shared/carousels/carousel-by-id";
import { withQueryParams } from "../hoc/with-query-params";
import ActionLogger from "action-logger";

class NoProducts extends React.Component {
  static DEAD_END_CAROUSEL_MODULE_TYPE = "dead_end_search_carousel";
  static propTypes = {
    carouselId: PropTypes.string,
    deadEndSearchData: PropTypes.object,
    deadEndSearchResultsObjType: PropTypes.string,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    dispatch: PropTypes.func,
    favorites: PropTypes.array,
    filters: PropTypes.object,
    hasAttributeFilters: PropTypes.bool,
    optionalButtonAction: PropTypes.func,
    optionalButtonText: PropTypes.string,
    queryParams: PropTypes.object,
    shortlists: PropTypes.array,
    subheadSize: PropTypes.string,
  };

  sentDeadEndPixelRef = React.createRef();

  componentDidMount() {
    this.shouldLogDeadEndPixel(); // first set of props
  }

  componentDidUpdate() {
    this.shouldLogDeadEndPixel(); // all preceding sets of props
  }

  carouselLoggingData() {
    return {
      objectType: "grid",
      eventType: "no_results",
    };
  }

  shouldLogDeadEndPixel() {
    if (this.isDeadEndDataComplete() && !this.sentDeadEndPixelRef.current) {
      this.triggerDeadEndPagePixels();
      this.sentDeadEndPixelRef.current = true;
    }
  }

  isDeadEndDataComplete() {
    // SJK [EXPLANATION] 08/22/2022: canonical sizes and zip code are always required to search
    const { deadEndSearchData } = this.props;
    if (!deadEndSearchData) return;
    return (
      deadEndSearchData.initialFilters?.includes("canonicalSizes") &&
      deadEndSearchData.initialFilters?.includes("zip_code")
    );
  }

  triggerDeadEndPagePixels() {
    ActionLogger.logAction(
      Object.assign(
        {
          object_type: this.props.deadEndSearchResultsObjType,
          action_type: "dead_end_page",
        },
        this.props.deadEndSearchData
      )
    );
  }

  triggerViewDesignersButtonPixels() {
    const { deadEndSearchResultsObjType, deadEndSearchData } = this.props;
    ActionLogger.inferAction(
      Object.assign(
        {
          object_type: deadEndSearchResultsObjType,
          action_type: "click_view_all_designers",
        },
        deadEndSearchData
      )
    );
  }

  triggerBrowseAllItemsButtonPixels() {
    const { deadEndSearchResultsObjType, deadEndSearchData } = this.props;
    ActionLogger.logAction(
      Object.assign(
        {
          object_type: deadEndSearchResultsObjType,
          action_type: "click_browse_all_items",
        },
        deadEndSearchData
      )
    );
  }

  handleButtonClick = () => {
    this.triggerBrowseAllItemsButtonPixels();
    if (this.props.optionalButtonAction) {
      this.props.optionalButtonAction();
    }
  };

  renderDesignersButton = key => {
    return (
      <a
        data-test-id="no-products-designer-btn"
        href="/designers"
        className="btn-secondary"
        key={key}
        onClick={() => {
          this.triggerViewDesignersButtonPixels();
        }}>
        View All Designers
      </a>
    );
  };

  renderOptionalButton = key => {
    if (this.props.optionalButtonText) {
      return (
        <button className="btn" data-test-id="no-products-optional-btn" onClick={this.handleButtonClick} key={key}>
          {this.props.optionalButtonText}
        </button>
      );
    }
  };

  hasTextSearch() {
    const { filters: { searchText } = {} } = this.props;

    return searchText && searchText.trim().length;
  }

  isDeadEndTextSearch() {
    const { hasAttributeFilters, queryParams: { deadEnd } = {} } = this.props;
    const inURL = !!deadEnd;

    return this.hasTextSearch() && (inURL || !hasAttributeFilters);
  }

  getSubtitle() {
    const { subheadSize = "medium" } = this.props;
    let text;

    if (this.isDeadEndTextSearch()) {
      text = "Double check your spelling, change up your filters, or try looking for a specific designer.";
    } else {
      text = "But you've still got options! Adjust your filters to see more items.";
    }

    if (text) {
      return (
        <p className={`universal-${subheadSize}--semibold`}>
          <span>{text}</span>
        </p>
      );
    }
  }

  renderCTAs() {
    const ctas = [];

    let key = 0;

    if (this.isDeadEndTextSearch()) {
      ctas.push(this.renderDesignersButton(key));
      key++;
    }

    ctas.push(this.renderOptionalButton(key));

    return <div className="grid-no-products-ctas">{ctas}</div>;
  }

  renderCarousel() {
    const { carouselId, dispatch, filters, deadEndSearchData } = this.props;
    const showCarousel = this.isDeadEndTextSearch();

    if (showCarousel && carouselId) {
      return (
        <div className="no-products-carousels" data-test-id="no-products-carousels">
          <CarouselById
            carouselId={carouselId}
            carouselLoggingData={this.carouselLoggingData()}
            carouselInGrid={true}
            deadEndSearchData={deadEndSearchData}
            dispatch={dispatch}
            filters={filters}
            displayedModal={this.props.displayedModal}
            favorites={this.props.favorites}
            shortlists={this.props.shortlists}
            deadEndCarousel={true}
          />
        </div>
      );
    }
  }

  renderMessage() {
    return (
      <div className="grid-no-products-message">
        <h1 className="grid-no-products-title">Sorry, we couldn’t find any matches.</h1>
        {this.getSubtitle()}
        {this.renderCTAs()}
      </div>
    );
  }

  render() {
    return (
      <>
        {this.renderMessage()}
        {this.renderCarousel()}
      </>
    );
  }
}

export default withQueryParams()(NoProducts);
